
import { defineComponent } from 'vue';

import IncorrectPermissions from '@/ChildrenComponents/IncorrectPermissions.vue';
import LoadingSpinner from '@/ChildrenComponents/LoadingSpinner.vue';
import ReportExporter from '@/ChildrenComponents/ReportExporter.vue';
import ValidationAlert from '@/ChildrenComponents/ValidationAlert.vue';

import { ComponentType } from '@/Enums/ComponentType';
import { LookupKeys } from '@/Enums/LookupKeys';

import { IInternalUser } from '@/Models/IInternalUser';
import { ITreatmentProgram } from '@/Models/ITreatmentProgram';
import { IBaseLookup } from '@/Models/Lookup/BaseLookup';
import { MasterLookupWrapper } from '@/Models/Lookup/MasterLookupWrapper';
import { IEconomicReport, IPhaseReportParamter } from '@/Models/Report/IPhaseReport';
import { IDataTableHeader } from '@/Models/IDataTableHeader';

import { doesUserRoleHaveComponentViewPermissions, isUserAdmin } from '@/Services/Helper/component-permissions-helper';
import { ReportService } from '@/Services/Report/report-service';
import { useUserStore } from '@/Services/Store/user-store';
import { TreatmentProgramService } from '@/Services/treatment-program-service';
import { ValidationHelper } from '@/Services/Helper/validation-helper';

export default defineComponent({
    name: 'economic-report',
    components: {
        LoadingSpinner,
        ValidationAlert,
        ReportExporter,
        IncorrectPermissions,
    },
    setup(): { currentUser: IInternalUser | null } {
        const userStoreInstance = useUserStore();
        return { currentUser: userStoreInstance.$state.cstInternalUser };
    },
    created() {
        this.isLoading = true;
        TreatmentProgramService.getAllTreatmentPrograms()
            .then((response) => {
                this.allPrograms = response;
                this.isLoading = false
            });
    },
    data() {
        return {
            lookups: {} as MasterLookupWrapper,
            reportData: [] as IEconomicReport[],
            validationResults: [] as string[],
            reportHeaders: [
                { title: "Participant Number", key: "participantId", sortable: true, align: "start", width: '100px' },
                { title: "First Name", key: "firstName", sortable: true, align: "start", width: "150px" },
                { title: "Last Name", key: "lastName", sortable: true, align: "start", width: "150px" },
                { title: "Treatment Program", key: "treatmentProgram", sortable: true, align: "start", width: '150px' },
                { title: "Employment Status at Entry", key: "employmentStatusAtEntry", align: "start", width: '150px' },
                { title: "Hourly Income at Entry", key: "hourlyIncomeAtEntry", align: "start", width: '150px' },
                { title: "Employment Status at Exit", key: "employmentStatusAtExit", sortable: true, align: "start" },
                { title: "Hourly Income at Exit", key: "hourlyIncomeAtExit", sortable: true, align: "start", width: '150px' },
                { title: "Number of Dependents Living with the Participant at Entry", key: "numberOfDependentsLivingWithParticipantAtEntry", sortable: true, align: "start", width: '150px' },
                { title: "Number of Dependents Living with the Participant at Exit", key: "numberOfDependentsLivingWithParticipantAtExit", sortable: true, align: "start", width: '150px' }
            ] as IDataTableHeader[],
            allPrograms: [] as ITreatmentProgram[],
            searchDto: {} as IPhaseReportParamter,
            searchValidationResults: [] as string[],
            requiredRules: ValidationHelper.requiredRules,

            isLoading: true,
            isLoadingResults: false,
        }
    },
    methods: {
        getReportData(): void {
            this.searchValidationResults = [];
            this.validationResults = [];
            this.isLoadingResults = true;

            if (this.searchDto.startDate == null) {
                this.searchValidationResults.push("A start date is required")
                this.isLoadingResults = false;
            }
            if (this.searchDto.endDate == null) {
                this.searchValidationResults.push("A end date is required")
                this.isLoadingResults = false;
            }

            if (this.searchValidationResults.length < 1) {
                ReportService.getEconomicReportData(this.searchDto)
                    .then(res => this.reportData = res)
                    .catch(() => this.validationResults.push("An error occurred while loading Economic Report, please try again"))
                    .finally(() => this.isLoadingResults = false)
            }
        },
        getLookupList(key: LookupKeys): IBaseLookup[] {
            return this.lookups.lookupLists[key];
        },
    },
    computed: {
        userAttachedPrograms(): Array<ITreatmentProgram> {
            return isUserAdmin(this.currentUser?.attachedRoles ?? []) ? this.allPrograms : this.currentUser?.attachedPrograms ?? []
        },
        localizedData(): any {
            return this.reportData.map((item) => {
                return {
                    ...item,
                }
            });
        },
        componentType(): ComponentType {
            return ComponentType.Report
        },
        canViewRolePermission(): boolean {
            return doesUserRoleHaveComponentViewPermissions(this.currentUser?.attachedRoles ?? [], this.componentType)
        },
    },
});
